import { useEffect, useState } from "react";
import { X, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, money, shortDate } from "../lib/api";

export default function StatementModal({ customer, currency, onRefund, onClose, entity = "customer" }) {
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const isSupplier = entity === "supplier";
  const labels = isSupplier
    ? { eyebrow: "STATEMENT", billed: "Billed", credited: "Debited", balance: "Payable" }
    : { eyebrow: "STATEMENT", billed: "Invoiced", credited: "Credited", balance: "Balance due" };

  useEffect(() => {
    api.get(`/${isSupplier ? "suppliers" : "customers"}/${customer.id}/statement`)
      .then((r) => setStatement(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load statement"))
      .finally(() => setLoading(false));
  }, [customer.id, isSupplier]);

  const totals = statement?.totals || {};

  return (
    <div className="modal-backdrop">
      <section className="create-modal" role="dialog" aria-modal="true" data-testid="statement-modal" style={{ maxWidth: 720 }}>
        <div className="modal-heading">
          <div>
            <p className="eyebrow">FLOWSPHERE / {labels.eyebrow}</p>
            <h2>{customer.name}</h2>
            <p>Account statement — {isSupplier ? "bills, payments and debit notes" : "invoices, payments and credit notes"}.</p>
          </div>
          <button className="icon-button" onClick={onClose} data-testid="statement-close-button"><X size={18} /></button>
        </div>

        {loading ? (
          <p style={{ padding: "24px 4px", color: "var(--muted, #708078)" }}>Loading statement…</p>
        ) : (
          <>
            <div className="statement-summary" data-testid="statement-summary">
              <div><span>{labels.billed}</span><strong>{money(totals.invoiced, currency)}</strong></div>
              <div><span>Paid</span><strong>{money(totals.paid, currency)}</strong></div>
              <div><span>{labels.credited}</span><strong className="statement-credit">−{money(totals.credited, currency)}</strong></div>
              <div className="statement-balance"><span>{totals.balance < 0 ? (isSupplier ? "Credit balance" : "Credit balance") : labels.balance}</span><strong>{totals.balance < 0 ? `${money(Math.abs(totals.balance), currency)} cr` : money(totals.balance, currency)}</strong></div>
            </div>

            <div className="table-wrap" style={{ marginTop: 8 }}>
              {(statement?.lines || []).length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon"><FileText size={22} /></div>
                  <h3>No transactions yet</h3>
                  <p>Invoices, payments and credit notes will appear here.</p>
                </div>
              ) : (
                <table data-testid="statement-table">
                  <thead>
                    <tr><th>Date</th><th>Type</th><th>Reference</th><th className="right">Debit</th><th className="right">Credit</th><th className="right">Balance</th></tr>
                  </thead>
                  <tbody>
                    {statement.lines.map((line, i) => (
                      <tr key={i} data-testid={`statement-line-${i}`}>
                        <td data-label="Date">{shortDate(line.date)}</td>
                        <td data-label="Type"><span className={`status ${line.type === "Credit Note" || line.type === "Debit Note" ? "returned" : line.type === "Payment" ? "paid" : "partially-paid"}`}>{line.type}</span></td>
                        <td data-label="Reference" className="mono">{line.ref || "—"}</td>
                        <td data-label="Debit" className="right">{line.debit ? money(line.debit, currency) : "—"}</td>
                        <td data-label="Credit" className="right">{line.credit ? money(line.credit, currency) : "—"}</td>
                        <td data-label="Balance" className="right"><strong>{money(line.balance, currency)}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        <div className="modal-actions">
          {!loading && totals.balance < 0 && onRefund && (
            <button type="button" className="secondary-button" data-testid="statement-refund-button" onClick={() => onRefund(statement?.customer || customer)}>
              Record refund ({money(Math.abs(totals.balance), currency)})
            </button>
          )}
          <button type="button" className="primary-button" onClick={onClose} data-testid="statement-done-button">Done</button>
        </div>
      </section>
    </div>
  );
}
